import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Users, CreditCard, BarChart2, Activity, Search, ChevronDown,
  Trash2, Shield, ShieldOff, RefreshCw, FileText, TrendingUp,
  DollarSign, UserCheck, UserX, AlertTriangle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/admin`;

const PLAN_COLORS = {
  free: 'bg-gray-100 text-gray-700',
  pro: 'bg-blue-100 text-blue-700',
  business: 'bg-purple-100 text-purple-700',
};

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-600',
  pending: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-green-100 text-green-700',
  declined: 'bg-red-100 text-red-700',
};

function StatCard({ icon: Icon, label, value, sub, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
    gray: 'bg-gray-50 text-gray-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-start gap-4">
      <div className={`p-3 rounded-lg ${colors[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function AdminPanel() {
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [payments, setPayments] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [userPage, setUserPage] = useState(0);
  const [docPage, setDocPage] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const PAGE_SIZE = 20;

  const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const loadStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/stats`, authHeader());
      setStats(r.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: userPage * PAGE_SIZE, limit: PAGE_SIZE };
      if (search) params.search = search;
      if (planFilter) params.plan = planFilter;
      const r = await axios.get(`${API}/users`, { ...authHeader(), params });
      setUsers(r.data.users);
      setTotalUsers(r.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [userPage, search, planFilter]);

  const loadPayments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/payments`, authHeader());
      setPayments(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadActiveUsers = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users/active`, authHeader());
      setActiveUsers(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/documents`, {
        ...authHeader(),
        params: { skip: docPage * PAGE_SIZE, limit: PAGE_SIZE }
      });
      setDocuments(r.data.documents);
      setTotalDocs(r.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [docPage]);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { if (tab === 'users') loadUsers(); }, [tab, loadUsers]);
  useEffect(() => { if (tab === 'billing') loadPayments(); }, [tab, loadPayments]);
  useEffect(() => { if (tab === 'active') loadActiveUsers(); }, [tab, loadActiveUsers]);
  useEffect(() => { if (tab === 'documents') loadDocuments(); }, [tab, loadDocuments]);

  const handlePlanChange = async (userId, plan) => {
    try {
      await axios.put(`${API}/users/${userId}/plan`, { plan }, authHeader());
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, plan } : u));
    } catch (e) { alert('Failed to update plan'); }
  };

  const handleToggleAdmin = async (userId, currentAdmin) => {
    try {
      await axios.put(`${API}/users/${userId}/admin`, { is_admin: !currentAdmin }, authHeader());
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: !currentAdmin } : u));
    } catch (e) { alert('Failed to update admin status'); }
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`, authHeader());
      setUsers(prev => prev.filter(u => u.id !== userId));
      setTotalUsers(prev => prev - 1);
      setConfirmDelete(null);
    } catch (e) { alert('Failed to delete user'); }
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'active', label: 'Active (30d)', icon: Activity },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'documents', label: 'Documents', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-sm text-gray-500">EZSig.com management dashboard</p>
          </div>
          <button
            onClick={() => { loadStats(); if (tab === 'users') loadUsers(); }}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="max-w-7xl mx-auto flex gap-0">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <t.icon className="w-4 h-4" />
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">

        {/* OVERVIEW TAB */}
        {tab === 'overview' && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={stats.total_users} color="blue" />
              <StatCard icon={DollarSign} label="Monthly Revenue" value={`$${stats.mrr}`} sub={`${stats.pro_users} Pro + ${stats.business_users} Business`} color="green" />
              <StatCard icon={UserCheck} label="Active (30 days)" value={stats.active_users_30d} sub={`${stats.new_users_30d} new this month`} color="orange" />
              <StatCard icon={FileText} label="Total Documents" value={stats.total_docs} sub={`${stats.docs_this_month} this month`} color="purple" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={TrendingUp} label="Pro Users" value={stats.pro_users} color="blue" />
              <StatCard icon={TrendingUp} label="Business Users" value={stats.business_users} color="purple" />
              <StatCard icon={UserX} label="Free Users" value={stats.free_users} color="gray" />
              <StatCard icon={Activity} label="Completed Docs" value={stats.completed_docs} sub={`${stats.total_ai_images} AI images`} color="green" />
            </div>

            {/* Plan breakdown */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h2 className="font-semibold text-gray-900 mb-4">Plan Distribution</h2>
              <div className="space-y-3">
                {[
                  { label: 'Free', count: stats.free_users, color: 'bg-gray-400' },
                  { label: 'Pro ($19/mo)', count: stats.pro_users, color: 'bg-blue-500' },
                  { label: 'Business ($49/mo)', count: stats.business_users, color: 'bg-purple-500' },
                ].map(p => (
                  <div key={p.label} className="flex items-center gap-3">
                    <div className="w-28 text-sm text-gray-600">{p.label}</div>
                    <div className="flex-1 bg-gray-100 rounded-full h-3">
                      <div
                        className={`h-3 rounded-full ${p.color}`}
                        style={{ width: `${stats.total_users ? (p.count / stats.total_users) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="w-10 text-sm font-medium text-gray-700 text-right">{p.count}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* USERS TAB */}
        {tab === 'users' && (
          <div className="space-y-4">
            {/* Filters */}
            <div className="flex gap-3">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={e => { setSearch(e.target.value); setUserPage(0); }}
                  className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <select
                value={planFilter}
                onChange={e => { setPlanFilter(e.target.value); setUserPage(0); }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">All Plans</option>
                <option value="free">Free</option>
                <option value="pro">Pro</option>
                <option value="business">Business</option>
              </select>
              <button onClick={loadUsers} className="text-sm text-blue-600 hover:text-blue-800 font-medium px-3 py-2 border border-blue-200 rounded-lg">
                Search
              </button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">{totalUsers} users total</span>
              </div>
              {loading ? (
                <div className="text-center py-12 text-gray-400">Loading...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Plan</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Docs</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Last Active</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Joined</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {users.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {u.name}
                            {u.is_admin && <span className="bg-red-100 text-red-700 text-xs px-1.5 py-0.5 rounded font-semibold">ADMIN</span>}
                          </div>
                          <div className="text-gray-500 text-xs">{u.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={u.plan}
                            onChange={e => handlePlanChange(u.id, e.target.value)}
                            className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${PLAN_COLORS[u.plan]}`}
                          >
                            <option value="free">Free</option>
                            <option value="pro">Pro</option>
                            <option value="business">Business</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {u.signed_docs}/{u.total_docs}
                          <span className="text-gray-400 text-xs ml-1">signed/total</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDateTime(u.last_active)}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(u.created_at)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleToggleAdmin(u.id, u.is_admin)}
                              title={u.is_admin ? 'Remove admin' : 'Make admin'}
                              className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700"
                            >
                              {u.is_admin ? <ShieldOff className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => setConfirmDelete(u)}
                              className="p-1.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {/* Pagination */}
              {totalUsers > PAGE_SIZE && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Showing {userPage * PAGE_SIZE + 1}–{Math.min((userPage + 1) * PAGE_SIZE, totalUsers)} of {totalUsers}
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={userPage === 0}
                      onClick={() => setUserPage(p => p - 1)}
                      className="px-3 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      disabled={(userPage + 1) * PAGE_SIZE >= totalUsers}
                      onClick={() => setUserPage(p => p + 1)}
                      className="px-3 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ACTIVE USERS TAB */}
        {tab === 'active' && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">{activeUsers.length} users active in the last 30 days</p>
            </div>
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              {loading ? (
                <div className="text-center py-12 text-gray-400">Loading...</div>
              ) : activeUsers.length === 0 ? (
                <div className="text-center py-12 text-gray-400">No active users in the last 30 days</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">User</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Plan</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Last Active</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Docs This Month</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {activeUsers.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900">{u.name}</div>
                          <div className="text-gray-500 text-xs">{u.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PLAN_COLORS[u.plan]}`}>
                            {u.plan}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{formatDateTime(u.last_active)}</td>
                        <td className="px-4 py-3 text-gray-600">{u.docs_this_month}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(u.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* BILLING TAB */}
        {tab === 'billing' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">Payment History</h2>
                <p className="text-xs text-gray-400 mt-0.5">
                  {payments.length > 0 && payments[0].source === 'stripe'
                    ? 'Live Stripe payment data'
                    : 'Subscription records (connect Stripe for live payment data)'}
                </p>
              </div>
              {loading ? (
                <div className="text-center py-12 text-gray-400">Loading...</div>
              ) : payments.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-200" />
                  <p>No payment records yet</p>
                  <p className="text-xs mt-1">Payments will appear here once Stripe is configured and customers subscribe</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Customer</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Amount</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">ID</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {payments.map(p => (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-gray-700">{p.customer_email || '—'}</td>
                        <td className="px-4 py-3 text-gray-600">{p.description || '—'}</td>
                        <td className="px-4 py-3 font-semibold text-gray-900">
                          ${Number(p.amount).toFixed(2)} {p.currency}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            p.status === 'succeeded' || p.status === 'active'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDateTime(p.created_at)}</td>
                        <td className="px-4 py-3 text-gray-400 text-xs font-mono">{p.id?.slice(0, 16)}…</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* DOCUMENTS TAB */}
        {tab === 'documents' && (
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">{totalDocs} documents total</span>
              </div>
              {loading ? (
                <div className="text-center py-12 text-gray-400">Loading...</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left">
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Document</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Owner</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Signers</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Created</th>
                      <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Completed</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {documents.map(d => (
                      <tr key={d.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-900 truncate max-w-xs">{d.filename}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-700">{d.user_name}</div>
                          <div className="text-gray-400 text-xs">{d.user_email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_COLORS[d.status] || 'bg-gray-100 text-gray-600'}`}>
                            {d.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{d.signed_count}/{d.signer_count}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(d.created_at)}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(d.completed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {totalDocs > PAGE_SIZE && (
                <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Showing {docPage * PAGE_SIZE + 1}–{Math.min((docPage + 1) * PAGE_SIZE, totalDocs)} of {totalDocs}
                  </span>
                  <div className="flex gap-2">
                    <button disabled={docPage === 0} onClick={() => setDocPage(p => p - 1)}
                      className="px-3 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">Previous</button>
                    <button disabled={(docPage + 1) * PAGE_SIZE >= totalDocs} onClick={() => setDocPage(p => p + 1)}
                      className="px-3 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50">Next</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-start gap-3 mb-4">
              <div className="p-2 bg-red-50 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete User</h3>
                <p className="text-sm text-gray-500 mt-1">
                  This will permanently delete <strong>{confirmDelete.name}</strong> ({confirmDelete.email}) and all their documents and data. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={() => handleDelete(confirmDelete.id)}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700">
                Delete User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
